import styled from 'styled-components';
import { useSettings } from '../../contexts/settingsContext';
import { useTranslation } from '../../contexts/translationContext';
import { ReasonQuestion, ReasonQuestionValue, formatDate, parseDate } from '../../utils/reasonQuestions';
import { theme } from '../../utils/theme';
import Typography from '../atoms/Typography';
import CheckBox from '../molecules/Checkbox';
import DatePicker from '../molecules/DatePicker';
import TextInput from '../molecules/TextInput';
import FormikDropdown from './form/FormikDropdown';

const StyleErrorMsg = styled(Typography)`
  padding-top: 8px;
  padding-left: 8px;
`;

const StyledHint = styled(Typography)`
  padding-top: 8px;
`;

const CheckboxToggle = styled.div`
  width: fit-content;
  cursor: pointer;
`;

type Props = {
  question: ReasonQuestion;
  value: ReasonQuestionValue | null;
  // Shown instead of the description once there is one.
  error: string | null;
  onChange: (value: ReasonQuestionValue | null) => void;
};

// The merchant's label and description are already translated, so none of them go through translate.
const ReasonQuestionInput = ({ question, value, error, onChange }: Props) => {
  const { translate } = useTranslation();
  const { cornerRadius } = useSettings();
  const label = question.required ? `${question.label} *` : question.label;
  const inputId = `reasonQuestion${question.id}`;

  const renderInput = () => {
    if (question.type === 'Text') {
      return (
        <TextInput
          title={label}
          maxLength={question.maxLength ?? undefined}
          value={(value as string) ?? ''}
          onChange={(event) => {
            onChange((event.target as HTMLInputElement).value);
          }}
          noTranslation
        />
      );
    }

    if (question.type === 'Select') {
      const noAnswer = question.required ? [] : [{ value: '', label: translate('reason_question_no_answer', null) }];
      return (
        <FormikDropdown
          name={inputId}
          error={undefined}
          translateKey={label}
          options={[
            ...noAnswer,
            ...question.options.map((option) => {
              return { value: option.id, label: option.label };
            }),
          ]}
          onChange={(option) => {
            onChange(option.value === '' ? null : option.value);
          }}
          value={value}
          className={undefined}
          noTranslation
        />
      );
    }

    if (question.type === 'DatePicker') {
      return (
        <>
          <Typography variant="input-label">{label}</Typography>
          <DatePicker
            required={question.required}
            onChange={(date) => {
              onChange(formatDate(date));
            }}
            value={parseDate(value)}
          />
        </>
      );
    }

    if (question.type === 'Checkbox') {
      // Space on the hidden input clicks it too, so the keyboard toggles through the same onClick.
      return (
        <CheckboxToggle
          onClick={() => {
            onChange(value !== true);
          }}
        >
          <CheckBox id={inputId} checked={value === true} label={label} labelKey={undefined} borderRadius={cornerRadius} />
        </CheckboxToggle>
      );
    }

    return null;
  };

  return (
    <>
      {renderInput()}
      {error && (
        <StyleErrorMsg color={theme.colors.red[80]} variant="input-error">
          {error}
        </StyleErrorMsg>
      )}
      {!error && question.description && (
        <StyledHint color={theme.colors.grey[80]} variant="body3">
          {question.description}
        </StyledHint>
      )}
    </>
  );
};

export default ReasonQuestionInput;
